import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

const toastVariants = {
  success: { 
    bg: 'bg-green-100 dark:bg-green-900 border-green-400 dark:border-green-600 text-green-700 dark:text-green-300', 
    icon: <CheckCircle className="h-5 w-5" /> 
  },
  danger: { 
    bg: 'bg-red-100 dark:bg-red-900 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300', 
    icon: <XCircle className="h-5 w-5" /> 
  },
  error: { 
    bg: 'bg-red-100 dark:bg-red-900 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300', 
    icon: <XCircle className="h-5 w-5" /> 
  },
  warning: { 
    bg: 'bg-yellow-100 dark:bg-yellow-900 border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300', 
    icon: <AlertCircle className="h-5 w-5" /> 
  },
  info: { 
    bg: 'bg-blue-100 dark:bg-blue-900 border-blue-400 dark:border-blue-600 text-blue-700 dark:text-blue-300', 
    icon: <Info className="h-5 w-5" /> 
  },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((message, variant = 'info') => {
    const id = Date.now();
    setToasts((prevToasts) => [
      ...prevToasts,
      { id, message, variant },
    ]);
    
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      removeToast(id);
    }, 3000);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center p-3 border rounded-md shadow-lg transition-all duration-300 ease-in-out ${toastVariants[toast.variant].bg} max-w-sm`}
          >
            <div className="flex-shrink-0 mr-3">
              {toastVariants[toast.variant].icon}
            </div>
            <div className="flex-1 text-sm font-medium">
              {toast.message}
            </div>
            <button
              type="button"
              className="ml-3 flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
              onClick={() => removeToast(toast.id)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};